import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { gql, useQuery } from "@apollo/client";

const GET_USER_SENSOR_SETTINGS = gql`
  query GetUserSensorSettings {
    getUserSensorSettings {
      id
      debugMode
      sensors {
        id
        name
        position
        isEnabled
        isSkipped
      }
    }
  }
`;

export const useKitchenSocket = (url) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const socketRef = useRef(null);

  // Get user sensor settings
  const { data: sensorSettingsData } = useQuery(GET_USER_SENSOR_SETTINGS, {
    errorPolicy: "ignore",
  });

  const connect = useCallback(() => {
    socketRef.current = io(url, {
      transports: ["websocket"],
      reconnectionAttempts: 3,
      reconnectionDelay: 3000,
      timeout: 10000,
    });

    socketRef.current.on("connect", () => {
      setIsConnected(true);
      setConnectionError(null);
    });

    socketRef.current.on("connect_error", (error) => {
      setIsConnected(false);
      setConnectionError(error.message);
    });

    socketRef.current.on("disconnect", (reason) => {
      setIsConnected(false);
      if (reason !== "io client disconnect") {
        setConnectionError("Connection lost");
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [url]);

  const emitWithTimeout = useCallback((event, data, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current?.connected) {
        return reject(new Error("Socket not connected"));
      }

      const timer = setTimeout(() => {
        reject(new Error("timeout"));
      }, timeout);

      socketRef.current.emit(event, data, (response) => {
        clearTimeout(timer);
        response.error ? reject(new Error(response.error)) : resolve(response);
      });
    });
  }, []);

  const checkSignals = useCallback(
    async (timeout = 50000) => {
      return new Promise((resolve, reject) => {
        if (!socketRef.current?.connected) {
          return reject(new Error("Kitchen not connected"));
        }

        const timer = setTimeout(() => {
          reject(new Error("timeout"));
        }, timeout);

        console.log("🔍 Emitting checkSignals to kitchen socket server...");

        // Emit checkSignals event to the socket server
        socketRef.current.emit("checkSignals", (signals) => {
          clearTimeout(timer);

          console.log(
            "📡 Received fresh signals from kitchen server:",
            signals
          );
          console.log("📊 Signals type:", typeof signals);
          console.log("📋 Signals length:", signals?.length);

          try {
            // Validate the response
            if (!Array.isArray(signals) || signals.length !== 30) {
              throw new Error(
                `Invalid signals response: expected array of 30, got ${typeof signals} with length ${
                  signals?.length
                }`
              );
            }

            // REVERSE SIGNAL ARRAY: Convert 0→1 and 1→0
            const reversedSignals = signals.map((signal) =>
              signal === 0 ? 1 : 0
            );
            console.log("🔄 Original signals:", signals);
            console.log("🔄 Reversed signals:", reversedSignals);

            // Get user sensor settings
            const userSettings = sensorSettingsData?.getUserSensorSettings;
            const sensorConfigs = userSettings?.sensors || [];

            // Create a map of position to sensor config for quick lookup
            const sensorConfigMap = new Map();
            sensorConfigs.forEach((sensor) => {
              sensorConfigMap.set(sensor.position, sensor);
            });

            // Check each signal using reversed signals
            const failedSensors = [];
            const skippedSensors = [];

            reversedSignals.forEach((signal, index) => {
              const position = index + 1; // Convert 0-based index to 1-based position
              const sensorConfig = sensorConfigMap.get(position);

              if (!sensorConfig) {
                // No configuration found - treat as unknown sensor
                if (signal === 0) {
                  failedSensors.push({
                    position,
                    name: `Unknown Sensor ${position}`,
                    signal,
                    reason: "No signal detected",
                  });
                }
                return;
              }

              // Skip if sensor is disabled or marked to skip
              if (!sensorConfig.isEnabled || sensorConfig.isSkipped) {
                skippedSensors.push({
                  position,
                  name: sensorConfig.name,
                  signal,
                  reason: sensorConfig.isSkipped
                    ? "Skipped by user"
                    : "Disabled by user",
                });
                return;
              }

              // Check signal for enabled sensors (now using reversed signals)
              if (signal === 0) {
                failedSensors.push({
                  position,
                  name: sensorConfig.name,
                  signal,
                  reason: "No signal detected",
                });
              }
            });

            // Log results for debugging
            console.log("Signal check results:", {
              timestamp: new Date().toISOString(),
              totalSignals: reversedSignals.length,
              failedSensors: failedSensors.length,
              skippedSensors: skippedSensors.length,
              allSignalsOk: failedSensors.length === 0,
            });

            if (userSettings?.debugMode) {
              console.log("Debug mode - Detailed signal analysis:", {
                originalSignals: signals,
                reversedSignals,
                failedSensors,
                skippedSensors,
                sensorConfigs: sensorConfigs.map((s) => ({
                  position: s.position,
                  name: s.name,
                  enabled: s.isEnabled,
                  skipped: s.isSkipped,
                })),
              });
            }

            // Create detailed response using reversed signals
            const response = {
              success: failedSensors.length === 0,
              totalSignals: reversedSignals.length,
              passedSignals: reversedSignals.filter((s, i) => {
                const position = i + 1;
                const config = sensorConfigMap.get(position);
                return (
                  s === 1 && config && config.isEnabled && !config.isSkipped
                );
              }).length,
              failedSensors,
              skippedSensors,
              rawSignals: userSettings?.debugMode ? reversedSignals : undefined,
              originalSignals: userSettings?.debugMode ? signals : undefined,
              timestamp: new Date().toISOString(),
              message:
                failedSensors.length === 0
                  ? "All enabled sensors are showing green signals"
                  : `${failedSensors.length} sensor(s) not showing green signal`,
            };

            console.log("✅ Processed kitchen socket response successfully");
            resolve(response);
          } catch (error) {
            console.error("❌ Error processing signal check:", error);
            reject(
              new Error(`Failed to process signal check: ${error.message}`)
            );
          }
        });
      });
    },
    [sensorSettingsData]
  );

  const resetSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    connect();
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [connect]);

  return {
    socket: socketRef.current,
    isConnected,
    connectionError,
    emitWithTimeout,
    checkSignals,
    resetSocket,
  };
};
